/**
 * Client-side photo compression for ticket uploads.
 *
 * Camera photos routinely weigh 8–20 MB, which used to trip the old
 * "over 5 MB" rejection and the proxy body limits. Instead of refusing
 * heavy photos, we downscale + re-encode them in the browser so ANY
 * photo the user picks uploads reliably (typically 150–600 KB JPEG).
 *
 * Falls back to the raw data URL if the browser can't decode the file
 * (rare formats) — the backend's per-photo cap is the final guard.
 */

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function decodeImage(file) {
  // createImageBitmap is fastest and handles EXIF orientation.
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (_) {
      /* fall through to <img> decode */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function drawScaled(source, maxDim, quality) {
  const w = source.width || source.naturalWidth;
  const h = source.height || source.naturalHeight;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", quality);
}

export async function fileToCompressedDataUrl(file) {
  // Most photos under 1.5 MB upload fine as-is — skip all processing
  // so adding them is instant.
  if (file.size < 1_500_000) {
    return readAsDataUrl(file);
  }
  let source;
  try {
    source = await decodeImage(file);
  } catch (_) {
    // Couldn't decode (unusual format) — send as-is and let the
    // backend cap decide.
    return readAsDataUrl(file);
  }
  try {
    let out = drawScaled(source, 1280, 0.78);
    if (out.length > 4_000_000) {
      out = drawScaled(source, 1024, 0.68);
    }
    // Canvas can produce a near-empty result on some corrupt inputs —
    // fall back to the raw file in that case.
    if (!out || out.length < 100) return readAsDataUrl(file);
    return out;
  } finally {
    if (source && typeof source.close === "function") source.close();
  }
}
